<template>
  <div v-if="state.status === 'not-financeable'" class="financing-calculator text-blue-whale">
    <p class="m-0 text-base">{{ t('components.financingCalculator.notFinanceable') }}</p>
  </div>
  <div v-else class="financing-calculator flex flex-col gap-4 text-blue-whale">
    <div class="grid gap-8 items-stretch lg:grid-cols-2">
      <!-- Terms -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3 px-3 py-2 border border-java-light rounded-sm">
          <!-- SharedPicture always fills its container, so the container sets the thumbnail size. -->
          <div v-if="image" class="w-16 h-11 flex-none overflow-hidden rounded-[6px] bg-neutral-lightest">
            <SharedPicture :media="image" image-class="w-full h-full object-cover" :alt="vehicleName" width="128" height="88" />
          </div>
          <div class="flex-1 min-w-0 flex flex-col">
            <span class="font-semibold text-[15px] truncate">{{ vehicleName }}</span>
            <span class="text-[13px] text-blue-whale-light">{{ t(showNet ? 'components.financingCalculator.cashPriceNet' : 'components.financingCalculator.cashPrice') }}</span>
          </div>
          <span class="font-chivo font-semibold text-lg text-accent whitespace-nowrap">{{ euro(net(price)) }}</span>
        </div>

        <div class="flex flex-col gap-2">
          <span class="font-semibold">{{ t('components.financingCalculator.customer.label') }}</span>
          <div class="grid gap-2 sm:grid-cols-2">
            <OptionCard :label="t('components.financingCalculator.customer.privat')" :selected="terms.customer === 'privat'" @select="choose('customer', 'privat')">
              <template #icon>
                <svg class="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" stroke-linecap="round" />
                </svg>
              </template>
            </OptionCard>
            <OptionCard :label="t('components.financingCalculator.customer.firma')" :selected="terms.customer === 'firma'" @select="choose('customer', 'firma')">
              <template #icon>
                <svg class="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <path d="M4 20V5.5L12 3v17M12 8.5l8 2.5v9M3 20h18M7 8h2M7 11.5h2M7 15h2M15 13h2M15 16h2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </template>
            </OptionCard>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <span class="font-semibold">{{ t('components.financingCalculator.mode.label') }}</span>
          <div class="grid gap-2 sm:grid-cols-2">
            <OptionCard v-if="leasable" :label="t('components.financingCalculator.mode.leasing')" :selected="terms.mode === 'leasing'" @select="choose('mode', 'leasing')">
              <template #icon>
                <svg class="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <circle cx="8" cy="15" r="4" />
                  <path d="m11 12 8-8M16 7l2 2M14 9l2 2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </template>
            </OptionCard>
            <OptionCard :label="t('components.financingCalculator.mode.kredit')" :selected="terms.mode === 'kredit'" @select="choose('mode', 'kredit')">
              <template #icon>
                <svg class="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3M4 7.5V17a2 2 0 0 0 2 2h14V9H6.5A2.5 2.5 0 0 1 4 7.5Z" stroke-linejoin="round" />
                  <circle cx="16" cy="14" r="1.2" fill="currentColor" stroke="none" />
                </svg>
              </template>
            </OptionCard>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <label :for="`${id}-down`" class="font-semibold">{{ t(`components.financingCalculator.downPayment.${terms.mode}`) }}</label>
          <input
            :id="`${id}-down`"
            type="number"
            inputmode="numeric"
            :value="terms.downPayment"
            :min="0"
            :max="maxDownPayment"
            :step="DOWN_PAYMENT_STEP"
            class="w-full h-12 px-4 border border-java-light rounded-sm text-base"
            @change="chooseTyped('downPayment', $event, maxDownPayment)"
          />
          <input
            type="range"
            :value="terms.downPayment"
            :min="0"
            :max="maxDownPayment"
            :step="DOWN_PAYMENT_STEP"
            class="w-full accent-accent m-0"
            :aria-label="t(`components.financingCalculator.downPayment.${terms.mode}`)"
            @input="terms.downPayment = valueOf($event)"
            @change="choose('downPayment', valueOf($event))"
          />
          <span class="text-[13px] text-blue-whale-light">{{ t('components.financingCalculator.downPayment.hint', { percent: downPaymentPercent }) }}</span>
        </div>

        <div class="grid gap-4 items-end sm:grid-cols-2">
          <div v-if="terms.mode === 'leasing'" class="flex flex-col gap-2">
            <label :for="`${id}-depot`" class="font-semibold">{{ t('components.financingCalculator.depot') }}</label>
            <input
              :id="`${id}-depot`"
              type="number"
              inputmode="numeric"
              :value="terms.depot"
              :min="0"
              :max="maxDepot"
              :step="DOWN_PAYMENT_STEP"
              class="w-full h-12 px-4 border border-java-light rounded-sm text-base"
              @change="chooseTyped('depot', $event, maxDepot)"
            />
          </div>
          <div v-else class="flex flex-col gap-2">
            <span class="font-semibold">{{ t('components.financingCalculator.finalPayment.label') }}</span>
            <div class="grid grid-cols-2 gap-2">
              <SharedButton
                :label="t('components.financingCalculator.finalPayment.with')"
                size="small"
                :variant="terms.withFinalPayment ? 'primary' : 'secondary'"
                @click="chooseFinalPayment(true)"
              />
              <SharedButton
                :label="t('components.financingCalculator.finalPayment.without')"
                size="small"
                :variant="terms.withFinalPayment ? 'secondary' : 'primary'"
                @click="chooseFinalPayment(false)"
              />
            </div>
          </div>
          <div v-if="terms.mode === 'leasing'" class="flex flex-col gap-2">
            <label :for="`${id}-km`" class="font-semibold">{{ t('components.financingCalculator.kmPerYear') }}</label>
            <select
              :id="`${id}-km`"
              :value="terms.kmPerYear"
              class="w-full h-12 px-4 border border-java-light rounded-sm text-base bg-white"
              @change="choose('kmPerYear', valueOf($event))"
            >
              <option v-for="km in KM_OPTIONS" :key="km" :value="km">{{ formatKm(km) }}</option>
            </select>
          </div>
        </div>

        <div class="flex flex-col gap-2 mt-auto">
          <span class="font-semibold">{{ t('components.financingCalculator.term') }}</span>
          <div class="grid grid-cols-4 gap-2">
            <SharedButton
              v-for="term in TERMS"
              :key="term"
              :label="String(term)"
              size="small"
              :variant="terms.termMonths === term ? 'primary' : 'secondary'"
              @click="choose('termMonths', term)"
            />
          </div>
        </div>
      </div>

      <!-- Representative example -->
      <div class="flex flex-col gap-2.5">
        <FinancingWarning />

        <div class="flex justify-between items-baseline gap-4 flex-wrap">
          <span class="font-semibold text-[17px]">{{ t(`components.financingCalculator.resultTitle.${terms.mode}`) }}</span>
          <a :href="`#${id}-legal`" class="text-[13px] text-blue-whale-light underline">{{ t('components.financingCalculator.representativeExample') }}</a>
        </div>

        <div v-if="needsTurnstile" class="flex flex-col gap-2 py-2">
          <p class="m-0 text-[15px]">{{ t('components.financingCalculator.turnstile') }}</p>
          <ClientOnly>
            <NuxtTurnstile :key="turnstileKey" v-model="turnstileToken" />
          </ClientOnly>
        </div>

        <div v-if="state.status === 'terms-rejected'" role="alert" class="flex flex-col gap-1 px-3.5 py-2.5 rounded-sm border border-christine text-[15px]">
          <span class="font-semibold">{{ t('components.financingCalculator.termsRejected') }}</span>
          <span v-for="error in state.errors" :key="error.field">{{ error.message }}</span>
        </div>

        <div
          v-if="state.status === 'unavailable'"
          role="alert"
          class="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 rounded-sm border border-christine text-[15px]"
        >
          <span>{{ t('components.financingCalculator.unavailable') }}</span>
          <SharedButton :label="t('components.financingCalculator.retry')" size="small" variant="secondary" @click="calculate(input)" />
        </div>

        <dl class="m-0 flex flex-col transition-opacity" :class="{ 'opacity-50': state.status === 'loading' }">
          <div v-for="row in rows" :key="row.label" class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-[3px] border-b border-neutral-lighter">
            <dt class="text-[15px] leading-snug">{{ row.label }}</dt>
            <dd class="m-0 text-[15px] leading-snug font-semibold text-right whitespace-nowrap">{{ row.value }}</dd>
            <span v-if="row.note" class="col-span-2 text-[13px] leading-snug">{{ row.note }}</span>
          </div>
        </dl>

        <div v-if="lastQuote" class="flex flex-col gap-2 pt-1.5 mt-auto">
          <SharedButton :label="t('components.financingCalculator.cta.inquire')" class="w-full" @click="emit('inquire', { input, quote: lastQuote })" />
          <div class="grid grid-cols-2 gap-2">
            <SharedButton :label="t('components.financingCalculator.cta.print')" variant="secondary" size="small" class="w-full" @click="emit('print')">
              <template #icon>
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <path d="M7 9V3.5h10V9M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v6.5H7V14Z" stroke-linejoin="round" />
                </svg>
              </template>
            </SharedButton>
            <SharedButton :label="t('components.financingCalculator.cta.save')" variant="secondary" size="small" class="w-full" @click="emit('save')">
              <template #icon>
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M4.5 19.5h15" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </template>
            </SharedButton>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-6 gap-y-2 pt-3 border-t border-neutral-lighter">
      <div class="flex items-center gap-2">
        <span class="text-[13px] text-blue-whale-light">{{ t('components.financingCalculator.partner') }}</span>
        <img src="/imgs/santander-logo.svg" alt="Santander Consumer Bank" class="h-[18px] w-auto block" width="103" height="18" />
      </div>
      <p :id="`${id}-legal`" class="m-0 flex-[1_1_420px] text-[13px] leading-normal">
        {{ t('components.financingCalculator.legal') }} {{ t(showNet ? 'components.financingCalculator.vatNote.net' : 'components.financingCalculator.vatNote.gross') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { FinancingInput, Quote } from '#shared/financing';
  import { isLeasable } from '#shared/leasable';
  import FinancingWarning from './Warning.vue';
  import OptionCard from './OptionCard.vue';

  /** The vehicle fields the calculator needs; a car detail fits. */
  export interface FinancingCalculatorVehicle {
    documentId: string;
    asking_price?: number | null;
    vat_deductible?: boolean | null;
    first_registration?: string | null;
    make?: { name?: string } | null;
    model?: string | null;
  }

  const props = defineProps<{ vehicle: FinancingCalculatorVehicle }>();
  const emit = defineEmits<{
    /** "Unverbindlich anfragen", with the terms and the example shown. */
    (e: 'inquire', payload: { input: FinancingInput; quote: Quote }): void;
    (e: 'print' | 'save'): void;
  }>();
  const { t } = useI18n();
  const id = useId();

  // Fixed steps, so visitors share Santander answers the server remembers (docs/santander-financing.md).
  const DOWN_PAYMENT_STEP = 500;
  const MAX_DOWN_PAYMENT_SHARE = 0.5;
  const DEFAULT_DOWN_PAYMENT_SHARE = 0.2;
  const MAX_DEPOT_SHARE = 0.3;
  const TERMS = [24, 36, 48, 60];
  const KM_OPTIONS = [10000, 15000, 20000, 25000, 30000];
  // Used for "with final payment" until Santander has recommended one for this car.
  const FALLBACK_FINAL_PAYMENT_PERCENT = 30;

  const roundToStep = (value: number) => Math.floor(value / DOWN_PAYMENT_STEP) * DOWN_PAYMENT_STEP;

  const leasable = computed(() => isLeasable(props.vehicle));
  // input and lastQuote are declared further down; the getters read them lazily.
  const { price, showNet, net, rows, euro, formatKm } = useFinancingExample(
    () => props.vehicle,
    () => input.value,
    () => lastQuote.value
  );
  const vehicleName = computed(() => [props.vehicle.make?.name, props.vehicle.model].filter(Boolean).join(' '));
  const image = computed(() => useVehicleImages(props.vehicle)[0]);
  const maxDownPayment = computed(() => roundToStep(price.value * MAX_DOWN_PAYMENT_SHARE));
  const maxDepot = computed(() => roundToStep(price.value * MAX_DEPOT_SHARE));

  const terms = reactive({
    customer: 'privat' as 'privat' | 'firma',
    mode: (leasable.value ? 'leasing' : 'kredit') as 'leasing' | 'kredit',
    downPayment: roundToStep(price.value * DEFAULT_DOWN_PAYMENT_SHARE),
    depot: 0,
    termMonths: 48,
    kmPerYear: 15000,
    withFinalPayment: false,
    finalPaymentPercent: FALLBACK_FINAL_PAYMENT_PERCENT,
  });

  const downPaymentPercent = computed(() => (price.value ? Math.round((terms.downPayment / price.value) * 100) : 0));

  const input = computed<FinancingInput>(() =>
    terms.mode === 'leasing'
      ? { mode: 'leasing', customer: terms.customer, downPayment: terms.downPayment, depot: terms.depot, termMonths: terms.termMonths, kmPerYear: terms.kmPerYear }
      : {
          mode: 'kredit',
          customer: terms.customer,
          downPayment: terms.downPayment,
          termMonths: terms.termMonths,
          ...(terms.withFinalPayment ? { finalPaymentPercent: terms.finalPaymentPercent } : {}),
        }
  );

  const { state, calculate, calculateDebounced, needsTurnstile, turnstileToken, turnstileKey } = useFinancingQuote(() => props.vehicle.documentId);
  onMounted(() => calculate(input.value));

  const valueOf = (event: Event) => Number((event.target as HTMLInputElement).value);

  /** "With final payment" uses Santander's recommendation from the latest credit answer. */
  function chooseFinalPayment(withIt: boolean) {
    const quote = lastQuote.value;
    if (withIt && quote?.mode === 'kredit' && quote.recommendedFinalPaymentPercent) {
      terms.finalPaymentPercent = quote.recommendedFinalPaymentPercent;
    }
    choose('withFinalPayment', withIt);
  }

  /** A typed amount, on change (blur or Enter): rounded to the steps, kept within its maximum, shown as used. */
  function chooseTyped(key: 'downPayment' | 'depot', event: Event, max: number) {
    const amount = Math.min(roundToStep(Math.max(0, valueOf(event) || 0)), max);
    (event.target as HTMLInputElement).value = String(amount);
    choose(key, amount);
  }

  /** A choice (button, select, slider release, typed amount): set it, and ask once the visitor pauses. */
  function choose<K extends keyof typeof terms>(key: K, value: (typeof terms)[K]) {
    terms[key] = value;
    calculateDebounced(input.value);
  }

  // While a new answer loads, the previous one stays visible (dimmed).
  const lastQuote = ref<Quote>();
  watch(state, current => {
    if (current.status === 'ready') lastQuote.value = current.quote;
  });
</script>
